"use client";

import React, { useState, useRef, useEffect } from "react";
import { Palette, Check, Sun, Moon } from "lucide-react";
import { useTheme } from "@/lib/theme/context";
import { useLanguage } from "@/lib/i18n/context";
import { ThemeId } from "@/lib/theme/themes";

export function ThemeSelector({ className = "" }: { className?: string }) {
  const { theme, themes, themeId, setTheme, isLight } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const lightThemes = themes.filter((tItem) => tItem.mode === "light");
  const darkThemes = themes.filter((tItem) => tItem.mode === "dark");

  const handleSelectTheme = (id: ThemeId) => {
    setTheme(id);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title={t("layout.theme.currentTheme", { name: theme.name, mode: theme.mode })}
        aria-label="Toggle theme selector"
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface-muted hover:bg-surface-cardHover text-themed-textPrimary border border-themed-border hover:border-themed-borderAccent text-xs font-semibold transition-all shadow-sm focus:outline-none"
      >
        <div className="flex items-center gap-1.5">
          {isLight ? (
            <Sun className="w-3.5 h-3.5 text-status-warning animate-pulse-subtle shrink-0" />
          ) : (
            <Moon className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0" />
          )}
          <span className="hidden sm:inline-block max-w-[120px] truncate">{theme.name}</span>
        </div>

        {/* 3-dot miniature swatch preview */}
        <div className="flex items-center -space-x-1 shrink-0 ml-0.5">
          <span
            className="w-2.5 h-2.5 rounded-full border border-themed-borderSubtle shadow-sm"
            style={{ backgroundColor: theme.swatches.background }}
          />
          <span
            className="w-2.5 h-2.5 rounded-full border border-themed-borderSubtle shadow-sm"
            style={{ backgroundColor: theme.swatches.accent }}
          />
        </div>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-surface-elevated border border-themed-border rounded-2xl shadow-2xl z-50 p-2.5 animate-in fade-in-50 zoom-in-95 duration-150 backdrop-blur-xl text-xs">
          <div className="px-2.5 py-1.5 border-b border-themed-borderSubtle flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-[var(--accent-primary)]" />
              <span className="font-bold text-themed-textPrimary tracking-tight">{t("layout.theme.title")}</span>
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-muted text-themed-textMuted font-mono">
              {themes.length} {t("layout.theme.palettes")}
            </span>
          </div>

          <div className="max-h-[360px] overflow-y-auto space-y-3 p-0.5">
            {/* Light Themes */}
            <div>
              <div className="px-2.5 py-1 text-[10px] font-bold text-themed-textMuted uppercase tracking-wider flex items-center gap-1.5">
                <Sun className="w-3 h-3 text-status-warning" />
                <span>{t("layout.theme.lightThemes")}</span>
              </div>
              <div className="space-y-1 mt-1">
                {lightThemes.map((tItem) => {
                  const isSelected = tItem.id === themeId;
                  return (
                    <button
                      key={tItem.id}
                      type="button"
                      onClick={() => handleSelectTheme(tItem.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all ${
                        isSelected
                          ? "bg-[var(--accent-primary)]/10 text-themed-textPrimary border border-[var(--accent-primary)]/30 font-bold shadow-sm"
                          : "text-themed-textSecondary hover:bg-surface-cardHover hover:text-themed-textPrimary"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                        <span className="text-sm shrink-0">{tItem.icon}</span>
                        <div className="min-w-0">
                          <p className="truncate text-xs">{tItem.name}</p>
                          <p className="text-[10px] text-themed-textMuted font-normal truncate">
                            {tItem.description}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {/* Swatches */}
                        <div className="flex items-center -space-x-1">
                          <span
                            className="w-3 h-3 rounded-full border border-themed-borderSubtle shadow-sm"
                            style={{ backgroundColor: tItem.swatches.background }}
                            title="Background"
                          />
                          <span
                            className="w-3 h-3 rounded-full border border-themed-borderSubtle shadow-sm"
                            style={{ backgroundColor: tItem.swatches.card }}
                            title="Card"
                          />
                          <span
                            className="w-3 h-3 rounded-full border border-themed-borderSubtle shadow-sm"
                            style={{ backgroundColor: tItem.swatches.accent }}
                            title="Accent"
                          />
                        </div>

                        {isSelected && (
                          <Check className="w-4 h-4 text-[var(--accent-primary)] shrink-0" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dark Themes */}
            <div>
              <div className="px-2.5 py-1 text-[10px] font-bold text-themed-textMuted uppercase tracking-wider flex items-center gap-1.5">
                <Moon className="w-3 h-3 text-[var(--accent-primary)]" />
                <span>{t("layout.theme.darkThemes")}</span>
              </div>
              <div className="space-y-1 mt-1">
                {darkThemes.map((t) => {
                  const isSelected = t.id === themeId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => handleSelectTheme(t.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all ${
                        isSelected
                          ? "bg-[var(--accent-primary)]/10 text-themed-textPrimary border border-[var(--accent-primary)]/30 font-bold shadow-sm"
                          : "text-themed-textSecondary hover:bg-surface-cardHover hover:text-themed-textPrimary"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                        <span className="text-sm shrink-0">{t.icon}</span>
                        <div className="min-w-0">
                          <p className="truncate text-xs">{t.name}</p>
                          <p className="text-[10px] text-themed-textMuted font-normal truncate">
                            {t.description}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {/* Swatches */}
                        <div className="flex items-center -space-x-1">
                          <span
                            className="w-3 h-3 rounded-full border border-themed-borderSubtle shadow-sm"
                            style={{ backgroundColor: t.swatches.background }}
                            title="Background"
                          />
                          <span
                            className="w-3 h-3 rounded-full border border-themed-borderSubtle shadow-sm"
                            style={{ backgroundColor: t.swatches.card }}
                            title="Card"
                          />
                          <span
                            className="w-3 h-3 rounded-full border border-themed-borderSubtle shadow-sm"
                            style={{ backgroundColor: t.swatches.accent }}
                            title="Accent"
                          />
                        </div>

                        {isSelected && (
                          <Check className="w-4 h-4 text-[var(--accent-primary)] shrink-0" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
